import React, {FC, useCallback} from 'react'
import {Button, ButtonPriority, Card, HoverStyles, PaddingModes, Text} from 'wix-ui-tpa/cssVars'
import {useTranslation} from '@wix/yoshi-flow-editor'
import {Experience as ExperienceProto} from '@wix/ambassador-table-reservations-v1-experience/types'
import {ReservationLocation as ReservationLocationProto} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {useSettings, useStyles} from '@wix/tpa-settings/react'

import {stylesParams} from '../../../stylesParams'
import {settingsParams} from '../../../settingsParams'
import {withMediaStorageUrl} from '../../../../../utils/images-gallery'
import {
  ImageSize,
  convertAlignmentNumberToString,
  convertImageSizeNumberToString,
} from '../../../../../types/createStylesParams'
import {convertMinutesToHoursAndMinutes} from '../../../../../utils/date'
import {TextAlignment, ButtonAlignment} from '../../../../../components-shared/Alignment'
import {useGetLayoutSize} from '../../../../../components-shared/LayoutSizeProvider'
import {ExperienceSchedule} from '../../../../../components-shared/ExperienceSchedule'
import {ExperiencePrice} from '../../../../../components-shared/ExperiencePrice'

import {classes, st} from './Experience.st.css'

interface Props {
  regionalSettings?: string
  reservationLocation: ReservationLocationProto
  experience: ExperienceProto
  showCoverImages: boolean
  handleGoToExperience: (experienceId: string) => void
}

export const Experience: FC<Props> = ({
  regionalSettings,
  reservationLocation,
  experience,
  showCoverImages,
  handleGoToExperience,
}) => {
  const {t} = useTranslation()
  const {layoutSize} = useGetLayoutSize()
  const styles = useStyles()
  const settings = useSettings()

  const {configuration, currency} = experience

  const showExperienceImage: boolean = settings.get(settingsParams.showExperienceImage)
  const showExperienceSummary: boolean = settings.get(settingsParams.showExperienceSummary)
  const showExperienceDuration: boolean = settings.get(settingsParams.showExperienceDuration)
  const showExperienceDate: boolean = settings.get(settingsParams.showExperienceDate)
  const showExperiencePrice: boolean = settings.get(settingsParams.showExperiencePrice)

  const reserveNowButtonText: string = settings.get(settingsParams.reserveNowButtonText)
  const learnMoreButtonText: string = settings.get(settingsParams.learnMoreButtonText)

  const experienceTextAlignment = convertAlignmentNumberToString(
    styles.get(stylesParams.experienceTextAlignment),
  )

  const experienceButtonAlignment = convertAlignmentNumberToString(
    styles.get(stylesParams.experienceButtonAlignment),
  )

  const experienceImageSize = convertImageSizeNumberToString(
    styles.get(stylesParams.experienceImageSize),
  )
  const imageId = configuration?.displayInfo?.coverImage?.id

  const businessSchedule = configuration?.onlineReservations?.businessSchedule
  const durationInMinutes = businessSchedule?.durationInMinutes

  const handleExperienceClick = useCallback(() => {
    if (experience.id) {
      handleGoToExperience(experience.id)
    }
  }, [experience.id, handleGoToExperience])

  return (
    <Card className={st(classes.card, {[layoutSize]: true})}>
      <Card.Container className={st(classes.cardContainer)}>
        <div className={st(classes.clickableContainer)} onClick={handleExperienceClick}>
          {showExperienceImage && showCoverImages ? (
            <div className={st(classes.imageContainer)}>
              <div
                className={st(classes.image)}
                style={{
                  backgroundImage: imageId ? `url(${withMediaStorageUrl(imageId)})` : undefined,
                  backgroundSize: experienceImageSize === ImageSize.Crop ? 'cover' : 'contain',
                }}
              />
            </div>
          ) : null}
          <div className={st(classes.content)}>
            <TextAlignment alignment={experienceTextAlignment}>
              <Text tagName="h2" className={st(classes.name)}>
                {configuration?.displayInfo?.name}
              </Text>
            </TextAlignment>
            <div className={st(classes.description)}>
              {showExperienceSummary ? (
                <TextAlignment alignment={experienceTextAlignment}>
                  <Text className={st(classes.summary)}>
                    {configuration?.displayInfo?.shortDescription}
                  </Text>
                </TextAlignment>
              ) : null}
              <TextAlignment alignment={experienceTextAlignment}>
                {showExperienceDuration && durationInMinutes ? (
                  <Text className={st(classes.details)}>
                    {t(
                      'uou-reservations.experiences-list.duration',
                      convertMinutesToHoursAndMinutes(durationInMinutes),
                    )}
                  </Text>
                ) : null}
                {showExperienceDuration && durationInMinutes && showExperienceDate ? (
                  <Text className={st(classes.details)}> | </Text>
                ) : null}
                {showExperienceDate ? (
                  <ExperienceSchedule
                    className={st(classes.details)}
                    businessSchedule={businessSchedule}
                    regionalSettings={regionalSettings}
                    timeZone={reservationLocation.location?.timeZone}
                  />
                ) : null}
              </TextAlignment>
              {showExperiencePrice ? (
                <TextAlignment alignment={experienceTextAlignment}>
                  <ExperiencePrice
                    className={st(classes.details)}
                    // TODO: temporary passing reservationLocation.currency as a fallback
                    currency={currency ?? reservationLocation.currency}
                    paymentPolicy={configuration?.paymentPolicy}
                  />
                </TextAlignment>
              ) : null}
            </div>
            <ButtonAlignment
              alignment={experienceButtonAlignment}
              className={st(classes.buttonContainer)}
            >
              <Button
                className={st(classes.button)}
                priority={ButtonPriority.primary}
                hoverStyle={HoverStyles.underline}
                paddingMode={PaddingModes.explicitPaddings}
              >
                {businessSchedule ? reserveNowButtonText : learnMoreButtonText}
              </Button>
            </ButtonAlignment>
          </div>
        </div>
      </Card.Container>
    </Card>
  )
}
